import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { academicService } from '../../services/academicService';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Users, GraduationCap, Calendar } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';

const AssignedClassesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch classes where staff is assigned as teacher or class teacher
      const res = await academicService.getClasses({ teacherId: user?.id });
      const classesData = res.data?.data?.classes || res.data?.data || [];
      setClasses(classesData);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load assigned classes');
      console.error('Error fetching classes:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (user?.id) {
      fetchClasses();
    }
  }, [user?.id]);

  const columns = [
    { 
      id: 'name', 
      header: 'Class Name', 
      accessorKey: 'name', 
      sortable: true 
    },
    { 
      id: 'level', 
      header: 'Level', 
      accessorKey: 'level', 
      sortable: true 
    },
    { 
      id: 'academicYear', 
      header: 'Academic Year', 
      accessorKey: 'academicYear', 
      sortable: true 
    },
    { 
      id: 'students', 
      header: 'Students', 
      accessorKey: 'studentCount',
      cell: ({ row }) => {
        const classData = row.original || row;
        if (!classData) return 0;
        const enrollmentCount = classData.enrollments?.length || 0;
        return enrollmentCount || classData.studentCount || 0;
      }
    },
    { 
      id: 'subjects', 
      header: 'Subjects', 
      accessorKey: 'subjects',
      cell: ({ row }) => {
        const classData = row.original || row;
        if (!classData) return 0;
        const subjects = classData.classSubjects || classData.subjects || [];
        return subjects.length;
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const classData = row.original || row;
        if (!classData?.id) return null;
        return (
          <Button 
            size="sm" 
            variant="outline"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click
              navigate(`/dashboard/academic/classes/${classData.id}`);
            }}
          >
            View Details
          </Button>
        );
      }
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchClasses}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">My Assigned Classes</h2>
          <p className="text-gray-600">Classes you are assigned to teach</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Classes</p>
                <p className="text-2xl font-bold text-gray-900">{classes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.reduce((sum, c) => sum + (c.enrollments?.length || c.studentCount || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <GraduationCap className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Subjects</p>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.reduce((sum, c) => sum + (c.classSubjects?.length || c.subjects?.length || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {classes.length === 0 ? (
        <EmptyState 
          icon={BookOpen}
          title="No assigned classes"
          description="You are not currently assigned to any classes."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <DataTable 
              data={classes} 
              columns={columns} 
              searchable 
              onRowClick={(row) => {
                // Handle row click - navigate to class details
                const classId = row?.id || row?.original?.id;
                if (classId) {
                  navigate(`/dashboard/academic/classes/${classId}`);
                }
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AssignedClassesPage;

